// hooks/useCommitteeFeedData.js - Hook for fetching committee-specific data
import { useState, useEffect, useCallback } from 'react';

const apiKey = process.env.REACT_APP_API_KEY

/**
 * useCommitteeFeedData - Hook for fetching committee materials using the committee API
 * 
 * This hook is specifically designed for the committee materials endpoint which uses
 * a different API pattern than standard feeds:
 * - Standard: /posts/recent?schema=X&table=Y
 * - Committee: /committees/?committee=X&chamber=Y
 * 
 * @param {string} apiEndpoint - The API endpoint parameters (e.g., '&skip=0&committee=Committee%20on%20Agriculture&chamber=HOUSE')
 * @param {number} limit - Number of items to fetch (default: 10)
 * @returns {Object} { data, loading, error, refetch }
 */
const useCommitteeFeedData = (apiEndpoint, limit = 10) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /**
   * Parse the API endpoint to extract committee parameters
   */
  const parseEndpoint = useCallback((endpoint) => {
    if (!endpoint) return null;
    
    try {
      // Clean the endpoint - remove leading & or ?
      let cleanEndpoint = endpoint;
      if (cleanEndpoint.startsWith('&') || cleanEndpoint.startsWith('?')) {
        cleanEndpoint = cleanEndpoint.substring(1);
      }
      
      const urlParams = new URLSearchParams(cleanEndpoint);
      
      return {
        committee: urlParams.get('committee'),
        chamber: urlParams.get('chamber'),
        skip: urlParams.get('skip') || '0'
      };
    } catch (err) {
      console.error('useCommitteeFeedData - Error parsing endpoint:', err);
      return null;
    }
  }, []);

  /**
   * Build the committee API URL
   */
  const buildApiUrl = useCallback(() => {
    const baseUrl = 'https://api.govglance.org';
    const params = parseEndpoint(apiEndpoint);
    
    if (!params) {
      // Return generic committee materials endpoint if no specific params
      return `${baseUrl}/committees/?limit=${limit}&skip=0`;
    }
    
    let url = `${baseUrl}/committees/?limit=${limit}&skip=${params.skip}`;
    
    if (params.committee) {
      url += `&committee=${encodeURIComponent(params.committee)}`;
    }
    
    if (params.chamber) {
      url += `&chamber=${encodeURIComponent(params.chamber)}`;
    }
    
    return url;
  }, [apiEndpoint, limit, parseEndpoint]);

  /**
   * Fetch data from the committee API
   */
  const fetchData = useCallback(async () => {
    if (!apiEndpoint) {
      setLoading(false);
      setError('No API endpoint provided');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const url = buildApiUrl();
      console.log('useCommitteeFeedData - Fetching:', url);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${apiKey}`,
        },
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      
      // Handle different response formats
      if (Array.isArray(result)) {
        setData(result);
      } else if (result.data && Array.isArray(result.data)) {
        setData(result.data);
      } else if (result.items && Array.isArray(result.items)) {
        setData(result.items);
      } else if (result.results && Array.isArray(result.results)) {
        setData(result.results);
      } else {
        // Assume the result itself is the data
        setData(result);
      }
      
    } catch (err) {
      console.error('useCommitteeFeedData - Error:', err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [apiEndpoint, buildApiUrl]);

  // Initial fetch
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Return data and utilities
  return {
    data,
    loading,
    error,
    refetch: fetchData
  };
};

export default useCommitteeFeedData;